<template>
    <div class="p-grid">
        <div class="p-col-12">
            <div class="card">
                <h4 class="p-d-flex p-ai-baseline">
                    <span>
                        <router-link v-if="client && client.key" :to="{ name: 'sites', params: { id: client.key } }">{{
                            client.name
                            }}</router-link> /
                        Asset Bin
                    </span>
                </h4>
                <DataTable :value="deletedAssets" :paginator="true" :rows="10" class="asset_list" ref="asset_table" selectionMode="multiple" :selection.sync="selectedRows" :filters="filters" :loading="loading" sortField="deleted_timestamp" :sortOrder="-1">
                    <template #header>
                        <div id="asset_filters">
                            <div id="asset_id_search">
                                <span>Asset ID</span>
                                <InputText v-model="filters['metadata.asset_id'].value" placeholder="Search Asset ID" />
                            </div>
                            <div id="global_search">
                                <span>Name</span>
                                <InputText v-model="filters['metadata.name'].value" placeholder="Search Name" />
                            </div>
                            <div class="select_filter">
                                <span>Site</span>
                                <MultiSelect v-model="filters['site_name'].value" :options="site_name_list" display="chip" filter placeholder="Select Site" class="p-column-filter select_filter" :disabled="site_name_list.length ? false : true" @show="triggerLabel()" @change="triggerChip()">
                                </MultiSelect>
                            </div>
                            <div class="select_filter">
                                <span>Category</span>
                                <MultiSelect v-model="filters['metadata.category'].value" :options="category_list" display="chip" filter placeholder="Select Category" class="p-column-filter select_filter" :disabled="category_list.length ? false : true" @show="triggerLabel()" @change="triggerChip()">
                                </MultiSelect>
                            </div>
                            <div class="select_filter">
                                <span>Type</span>
                                <MultiSelect v-model="filters['metadata.asset_type'].value" :options="asset_type_list" display="chip" filter placeholder="Select Type" class="p-column-filter select_filter" :disabled="asset_type_list.length ? false : true" @show="triggerLabel()" @change="triggerChip()">
                                </MultiSelect>
                            </div>
                            <div class="select_filter">
                                <span>Sub-Area</span>
                                <MultiSelect v-model="filters['metadata.location'].value" :options="location_list" display="chip" filter placeholder="Select Sub-Area" class="p-column-filter select_filter" :disabled="location_list.length ? false : true" @show="triggerLabel()" @change="triggerChip()">
                                </MultiSelect>
                            </div>
                            <div class="select_filter">
                                <span>Area</span>
                                <MultiSelect v-model="filters['metadata.area'].value" :options="area_list" display="chip" filter placeholder="Select Area" class="p-column-filter select_filter" :disabled="area_list.length ? false : true" @show="triggerLabel()" @change="triggerChip()">
                                </MultiSelect>
                            </div>
                            <div class="select_filter">
                                <span>Make</span>
                                <MultiSelect v-model="filters['metadata.make'].value" :options="make_list" display="chip" filter placeholder="Select Make" class="p-column-filter select_filter" :disabled="make_list.length ? false : true" @show="triggerLabel()" @change="triggerChip()">
                                </MultiSelect>
                            </div>
                            <div class="select_filter">
                                <span>Model</span>
                                <MultiSelect v-model="filters['metadata.model'].value" :options="model_list" display="chip" filter placeholder="Select Model" class="p-column-filter select_filter" :disabled="model_list.length ? false : true" @show="triggerLabel()" @change="triggerChip()">
                                </MultiSelect>
                            </div>
                            <div class="select_filter select_filter--date">
                                <span>Deletion Date</span>
                                <div>
                                    <i class="pi pi-calendar-plus" style="font-size: 16px"></i>
                                    <Calendar @hide="triggerClose(deleteDatePreParse, 'deleted_timestamp')" v-model="deleteDatePreParse" :manualInput="false" selectionMode="range" placeholder="Select Date Range" dateFormat="d M yy" style="flex-grow: 1;" />
                                </div>
                            </div>
                        </div>
                        <div id="button_row">
                            <Button type="button" icon="pi pi-replay" label="Reset Filters" id="reset_filters" @click.prevent="clearFilter()" />
                            <Button type="button" :label="selectedRows.length == 1 ? 'Restore Asset' : 'Restore Assets'" icon="pi pi-history" @click.prevent="displayConfirmation = true" :disabled="selectedRows.length > 0 ? false : true" />
                        </div>
                    </template>
                    <template #empty>
                        No assets found.
                    </template>
                    <template #loading>
                        Loading asset data. Please wait.
                    </template>
                    <!-- <Column selectionMode="multiple" headerClass="table__button--header" bodyClass="table__button" /> -->
                    <Column field="metadata.asset_id" header="ID" headerClass="id_column" sortable>
                        <template #body="slotProps">
                            <span>{{ forceFourDigits(slotProps.data.metadata.asset_id) }}</span>
                        </template>
                    </Column>
                    <Column field="site_name" header="Site" headerClass="site_column" sortable>
                        <template #body="slotProps">
                            {{ slotProps.data.site_name }}
                        </template>
                    </Column>
                    <Column field="metadata.name" header="Name" headerClass="name_column" sortable>
                        <template #body="slotProps">
                            <span v-if="slotProps.data.metadata.name">
                                {{ slotProps.data.metadata.name }}
                            </span>
                            <span v-else>
                                <i>No Name Assigned</i>
                            </span>
                        </template>
                    </Column>
                    <Column field="metadata.asset_type" header="Type" headerClass="type_column" sortable>
                        <template #body="slotProps">
                            <span v-if="slotProps.data.metadata.asset_type">{{ slotProps.data.metadata.asset_type }}</span>
                            <span v-else><i>No Type Assigned</i></span>
                        </template>
                    </Column>
                    <Column field="metadata.category" header="Category" sortable>
                        <template #body="slotProps">
                            <span v-if="slotProps.data.metadata.category">{{ slotProps.data.metadata.category }}</span>
                            <span v-else>
                                <i v-if="slotProps.data.metadata.asset_type == 'Asset'">No Category Assigned</i>
                                <i v-else>N/A</i>
                            </span>
                        </template>
                    </Column>
                    <Column field="metadata.area" header="Area" sortable>
                        <template #body="slotProps">
                            <span v-if="slotProps.data.metadata.area">{{ slotProps.data.metadata.area }}</span>
                            <span v-else><i>No Area Assigned</i></span>
                        </template>
                    </Column>
                    <Column field="metadata.location" header="Sub-Area" sortable>
                        <template #body="slotProps">
                            <span v-if="slotProps.data.metadata.location">{{ slotProps.data.metadata.location }}</span>
                            <span v-else><i>No Sub-Area Assigned</i></span>
                        </template>
                    </Column>
                    <Column field="metadata.make" header="Make" sortable>
                        <template #body="slotProps">
                            <span v-if="slotProps.data.metadata.make">{{ slotProps.data.metadata.make }}</span>
                            <span v-else>
                                <i v-if="slotProps.data.metadata.asset_type == 'Asset'">No Make Assigned</i>
                                <i v-else>N/A</i>
                            </span>
                        </template>
                    </Column>
                    <Column field="metadata.model" header="Model" sortable>
                        <template #body="slotProps">
                            <span v-if="slotProps.data.metadata.model">{{ slotProps.data.metadata.model }}</span>
                            <span v-else>
                                <i v-if="slotProps.data.metadata.asset_type == 'Asset'">No Model Assigned</i>
                                <i v-else>N/A</i>
                            </span>
                        </template>
                    </Column>
                    <Column field="deleted_timestamp" header="Deletion Date" sortable>
                        <template #body="slotProps">
                            {{ format_date(slotProps.data.deleted_timestamp.seconds * 1000) }}
                        </template>
                    </Column>
                </DataTable>
            </div>
        </div>
        <!-- restore assets dialog -->
        <Dialog header="Confirmation" :visible.sync="displayConfirmation" :style="{ width: '50vw' }" :modal="true">
            <div class="p-d-flex p-ai-center">
                <i class="pi pi-exclamation-triangle p-mr-3" style="font-size: 2rem" />
                <span>Are you sure you want to restore these assets?</span>
            </div>
            <template #footer>
                <div class="p-d-flex p-ai-center p-jc-between" style="gap: 2rem;">
                    <Button label="Restore Asset to previous site" icon="pi pi-check" @click="triggerRestore()" class="p-button-primary" autofocus />

                    <div class="p-ai-end" style="gap: 1rem; display: inline-flex;">
                        <Dropdown v-model="selectedSite" :options="availableSites" class="selector_label" placeholder="Restore to another Site" optionLabel="name" />
                        <Button type="button" icon="pi pi-check" class="p-button-primary" style="height:fit-content; bottom:1px; width: 2.357rem;" @click="triggerRestore(selectedSite)" :disabled="selectedSite ? false : true" />
                    </div>
                </div>
            </template>
        </Dialog>
        <Toast />
    </div>
</template>

<script>
import { db } from '@/firebase';
import moment from 'moment';
import { mapGetters } from "vuex";
import { FilterMatchMode } from 'primevue/api';
import axios from 'axios'

export default {
    data() {
        return {
            deleteDatePreParse: null,
            filters: {
                'metadata.name': { value: null, matchMode: FilterMatchMode.CONTAINS },
                site_name: { value: null, matchMode: FilterMatchMode.IN },
                'metadata.asset_id': { value: null, matchMode: FilterMatchMode.CONTAINS },
                'metadata.asset_type': { value: null, matchMode: FilterMatchMode.IN },
                'metadata.category': { value: null, matchMode: FilterMatchMode.IN },
                'metadata.make': { value: null, matchMode: FilterMatchMode.IN },
                'metadata.model': { value: null, matchMode: FilterMatchMode.IN },
                'metadata.location': { value: null, matchMode: FilterMatchMode.IN },
                'metadata.area': { value: null, matchMode: FilterMatchMode.IN },
                'deleted_timestamp.seconds': { value: [], matchMode: FilterMatchMode.BETWEEN },
            },
            // bulk editing
            selectedRows: [],
            // bulk editing end
            name_list: [],
            category_list: [],
            make_list: [],
            model_list: [],
            location_list: [],
            area_list: [],
            site_name_list: [],
            asset_type_list: [],
            // filtering data end
            loading: true,
            site: {},
            deletedAssets: [],
            client: {},
            visibleRight: false,
            availableSitesKeys: [],
            availableSites: [],
            displayConfirmation: false,
            selectedSite: ''
        }
    },
    firestore() {
        return {
            client: db.collection('clients').doc(this.$route.params.client_id),
            deletedAssets: {
                ref: db.collection('asset_bin').where('metadata.client_id', '==', this.$route.params.client_id),
                resolve: (data) => {
                    data.forEach((item) => {
                        if (item.metadata.name) {
                            this.name_list.push(item.metadata.name)
                            this.name_list = [...new Set(this.name_list)]; // clears duplicates
                        }
                        if (item.site_name) {
                            this.site_name_list.push(item.site_name)
                            this.site_name_list = [...new Set(this.site_name_list)]; // clears duplicates
                        }
                        if (item.metadata.category) {
                            this.category_list.push(item.metadata.category)
                            this.category_list = [...new Set(this.category_list)]; // clears duplicates
                        }
                        if (item.metadata.asset_type) {
                            this.asset_type_list.push(item.metadata.asset_type)
                            this.asset_type_list = [...new Set(this.asset_type_list)]; // clears duplicates
                        }
                        if (item.metadata.make) {
                            this.make_list.push(item.metadata.make)
                            this.make_list = [...new Set(this.make_list)]; // clears duplicates
                        }
                        if (item.metadata.model) {
                            this.model_list.push(item.metadata.model)
                            this.model_list = [...new Set(this.model_list)]; // clears duplicates
                        }
                        if (item.metadata.location) {
                            this.location_list.push(item.metadata.location)
                            this.location_list = [...new Set(this.location_list)]; // clears duplicates
                        }
                        if (item.metadata.area) {
                            this.area_list.push(item.metadata.area)
                            this.area_list = [...new Set(this.area_list)]; // clears duplicates
                        }
                    })
                    this.name_list.unshift('');
                    this.site_name_list.unshift('');
                    this.category_list.unshift('');
                    this.asset_type_list.unshift('');
                    this.make_list.unshift('');
                    this.model_list.unshift('');
                    this.location_list.unshift('');
                    this.area_list.unshift('');

                    this.loading = false;
                }
            },
            sites: {
                ref: db.collection('sites').where('client_id', '==', this.$route.params.client_id),
                resolve: (data) => {
                    // not sure why, but the first item in the list is not being added to the list
                    // most likley and issue with primevue
                    this.availableSites.push({
                        name: 'Restore to previous site',
                        key: ''
                    })
                    data.forEach((item, index) => {
                        this.availableSitesKeys.push(item.key)
                        this.availableSites.push({
                            name: item.name,
                            key: item.key
                        })
                    })
                }
            },
        }
    },
    computed: {
        // map `this.user` to `this.$store.getters.user`
        ...mapGetters({
            user: "user"
        })
    },
    methods: {
        triggerRestore(selectedSiteObject) {
            let iterator = 0,
                assetsWithValidSite = [];

            this.selectedRows.forEach(row => {
                let rowKey = row.key;

                db.collection('asset_bin').doc(rowKey).get().then((asset) => {
                    let metadata = asset.data().metadata; // access data from stored asset

                    // console.log(selectedSiteObject);
                    if (selectedSiteObject && (selectedSiteObject.key != metadata.site_id)) { // if selected site doesn't match what's currently in the asset, overwrite
                        metadata.site_id = selectedSiteObject.key // this ensures the asset displays in the new site
                        metadata.restored_from_other_site = true
                    }

                    if (this.availableSitesKeys.includes(metadata.site_id)) { // if site still exists, push back normally

                        assetsWithValidSite.push(rowKey);

                        db.collection('assets').doc(rowKey).set(metadata).then(() => { // set asset back to normal
                            db.collection('asset_bin').doc(rowKey).delete().then(() => { // delete asset from bin
                                iterator++;
                                if (iterator == assetsWithValidSite.length) { // on final item in selected rows, confirm completion
                                    db.collection('logs').add({
                                        activity: 'Assets Restored',
                                        timestamp: new Date(),
                                        user: this.user.data.email,
                                        client_id: this.$route.params.client_id,
                                        site: 'Restored from ' + asset.data().site_name
                                    }).then(() => {

                                        db.collection('faults').where('asset_id', '==', rowKey).get().then((faults) => {
                                            console.log(faults)
                                            faults.docs.forEach((fault) => {
                                                db.collection('faults').doc(fault.id).update({ asset_deleted: false })
                                            })
                                        })

                                        const filterSetUrl = process.env.VUE_APP_API_URL + '/filter/set';

                                        axios({
                                            method: 'post',
                                            url: filterSetUrl,
                                            headers: {
                                                'Authorization': `Bearer ${this.user.auth.accessToken}`
                                            },
                                            data: {
                                                'siteId': metadata.site_id,
                                            }
                                        }).then((response) => {
                                            console.log(response.data)
                                        }).catch(error => { console.error(error) });

                                        this.$toast.add({ severity: 'success', summary: 'Success', detail: 'Assets Restored', life: 3000 });
                                        this.displayConfirmation = false;
                                        this.toggleSidebar = false;
                                        iterator = 0;
                                        this.selectedRows = [];
                                        this.selectedSite = [];
                                        assetsWithValidSite = [];
                                    });
                                }
                            });
                        });
                    } else {
                        this.$toast.add({ severity: 'error', summary: 'Error', detail: 'Site no longer exists: ' + asset.data().site_name + '. To restore this Asset, please select a different Site', life: 3000 });
                    }

                });

            })
        },
        triggerClose(value, selector) {
            // console.log(value)
            let array = [];
            value.forEach(function (timestamp, index) {
                let formattedDate = new Date(timestamp)
                timestamp = formattedDate.getTime();
                array.push(timestamp / 1000);
            });
            this.filters['deleted_timestamp.seconds'].value = array;
        },
        format_date(value) {
            if (!isNaN(value)) {
                return moment(value).format('DD/MM/YYYY')
            } else {
                return;
            }
        },
        triggerChip() {
            let labels = document.querySelectorAll('.p-multiselect-label');

            labels.forEach((span) => {
                setTimeout(function () {
                    let tokens = span.querySelectorAll('.p-multiselect-token');
                    if (tokens.length) {
                        tokens.forEach((token) => {
                            let span = token.querySelector('.p-multiselect-token-label');
                            if (span.innerHTML == '') {
                                span.innerHTML = 'Unassigned'; // overwrites empty string
                            }
                        })
                    }
                }, 1) // timeout required due to toolkit adding chip to label
            })
        },
        triggerLabel() {
            let emptySpan = event.currentTarget.querySelector('.p-multiselect-item > span:first-of-type'); // selects first item
            emptySpan.innerHTML = 'Unassigned';
        },
        clearFilter() {
            this.deleteDatePreParse = [];
            this.filters = {
                'metadata.name': { value: null, matchMode: FilterMatchMode.CONTAINS },
                site_name: { value: null, matchMode: FilterMatchMode.IN },
                'metadata.asset_id': { value: null, matchMode: FilterMatchMode.CONTAINS },
                'metadata.asset_type': { value: null, matchMode: FilterMatchMode.IN },
                'metadata.category': { value: null, matchMode: FilterMatchMode.IN },
                'metadata.make': { value: null, matchMode: FilterMatchMode.IN },
                'metadata.model': { value: null, matchMode: FilterMatchMode.IN },
                'metadata.location': { value: null, matchMode: FilterMatchMode.IN },
                'metadata.area': { value: null, matchMode: FilterMatchMode.IN },
                'deleted_timestamp.seconds': { value: [], matchMode: FilterMatchMode.BETWEEN },
            };
        },
        forceFourDigits(val) {
            // add zeroes to 
            var numStr = val.toString();
            while (numStr.length < 4) {
                // keep adding zeroes until length is 4
                numStr = "0" + numStr;
            }
            return numStr;
        },
    }
}
</script>
